import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { logger } from "@octopusdeploy/logging";
import type { ActionTemplateParameterResource, GetScopedProjectTemplateValuesForProjectBffResponse, ProjectResource, ScopedProjectTemplateTenantValue, VariableType, WorkerPoolResource, EnvironmentResource, } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { Dictionary } from "lodash";
import * as React from "react";
import { useState } from "react";
import type { ProjectTenantVariablesFilterState } from "~/areas/projects/components/Variables/TenantVariables/FiltersBar";
import { ProjectTenantVariableTemplatesFiltersBar } from "~/areas/projects/components/Variables/TenantVariables/FiltersBar";
import { MissingValuesCallout } from "~/areas/projects/components/Variables/TenantVariables/MissingValuesCallout";
import NoTemplatesOnboarding from "~/areas/projects/components/Variables/TenantVariables/NoTemplatesOnboarding";
import NoValuesOnboarding from "~/areas/projects/components/Variables/TenantVariables/NoValuesOnboarding";
import ProjectTemplatesSidebarContent from "~/areas/projects/components/Variables/TenantVariables/ProjectTemplatesSidebarContent";
import ScopedProjectTemplateScopeInlineCell from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/ScopedProjectTemplateScopeInlineCell";
import { type ScopedProjectTemplatesScopeChangeTracker, type ScopedProjectTemplatesScopeKey } from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/useScopedProjectTemplatesScopeChangeTracker";
import { TemplateNameCell } from "~/areas/projects/components/Variables/TenantVariables/TemplateNameCell";
import { buildScopedEnvironmentIdsByGroup } from "~/areas/projects/components/Variables/TenantVariables/buildScopedEnvironmentIdsByGroup";
import { getVariableTypeFromDisplaySettings } from "~/areas/projects/components/Variables/TenantVariables/getVariableTypeFromDisplaySettings";
import { pagingBarStyles, styles, tenantVariablesDataTable } from "~/areas/projects/components/Variables/TenantVariables/styles";
import { extractProjectTenantVariableIds, createProjectTenantVariableKey } from "~/areas/projects/components/Variables/TenantVariables/useProjectTenantVariableValueChangeTracker";
import type { ProjectTenantVariableValueChangeTracker, ProjectTenantVariableValueKey } from "~/areas/projects/components/Variables/TenantVariables/useProjectTenantVariableValueChangeTracker";
import type { TenantVariableFiltersData } from "~/areas/projects/components/Variables/TenantVariables/useTenantVariableFiltersData";
import type { TenantVariableResourceOptions } from "~/areas/projects/components/Variables/TenantVariables/useTenantVariableResourceOptions";
import { TenantNameCellWithLink } from "~/areas/tenants/components/DataTable/Cells/TenantNameCell";
import NumberedPagingBar from "~/areas/tenants/components/Paging/NumberedPagingBar";
import { type DoBusyTask } from "~/components/DataBaseComponent/index";
import { GroupedDataTable } from "~/components/GroupedDataTable/GroupedDataTable";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import SidebarLayout, { SidebarSide } from "~/components/SidebarLayout/index";
import { generateGuid } from "../../../Process/generation";
import ScopedProjectTemplateValueInlineCell from "./ScopedProjectTemplateValueInlineCell";
interface ScopedProjectTemplatesInlineTabContentProps {
    project: ProjectResource;
    connectedEnvironments: Map<string, EnvironmentResource[]>;
    doBusyTask: DoBusyTask;
    filterState: ProjectTenantVariablesFilterState;
    setFilterState: (filter: ProjectTenantVariablesFilterState) => void;
    filterData: TenantVariableFiltersData;
    pageSize: number;
    setPageState: (newPageNumber: number, newPageSize: number) => void;
    projectTemplatesWithValues: GetScopedProjectTemplateValuesForProjectBffResponse;
    onAddTemplateFromOnboarding: (text: string) => void;
    resourceOptions: TenantVariableResourceOptions;
    variableValueTracker: ProjectTenantVariableValueChangeTracker;
    scopeChangeTracker: ScopedProjectTemplatesScopeChangeTracker;
}
export default function ScopedProjectTemplatesInlineTabContent({ project, connectedEnvironments, doBusyTask, filterState, filterData, setFilterState, pageSize, setPageState, projectTemplatesWithValues, onAddTemplateFromOnboarding, resourceOptions, variableValueTracker, scopeChangeTracker, }: ScopedProjectTemplatesInlineTabContentProps) {
    if (project.Templates.length === 0) {
        return <NoTemplatesOnboarding project={project} doBusyTask={doBusyTask} onAddTemplate={onAddTemplateFromOnboarding}/>;
    }
    const missingValuesTemplateIds = new Set<string>(projectTemplatesWithValues.MissingValueProjectTemplateIds);
    if (projectTemplatesWithValues.TotalValuesCount === 0) {
        return (<div style={{ height: "100%" }}>
                <SidebarLayout sideBar={<ProjectTemplatesSidebarContent project={project} pageSize={pageSize} doBusyTask={doBusyTask} filterState={filterState} setFilterState={setFilterState} missingValueTemplateIds={missingValuesTemplateIds}/>} side={SidebarSide.Left} extendSidebarToEdges={true} extendContentToEdges={true} reduceWidthForSmallResolution={true}>
                    <NoValuesOnboarding project={project}/>
                </SidebarLayout>
            </div>);
    }
    const variableValues = buildScopedProjectTemplateRowData(project, projectTemplatesWithValues, variableValueTracker, scopeChangeTracker);
    const unsavedChanges = variableValueTracker.values().length || scopeChangeTracker.values().length;
    return (<SidebarLayout sideBar={<ProjectTemplatesSidebarContent project={project} pageSize={pageSize} doBusyTask={doBusyTask} filterState={filterState} setFilterState={setFilterState} missingValueTemplateIds={missingValuesTemplateIds}/>} side={SidebarSide.Left} extendSidebarToEdges={true} extendContentToEdges={true} reduceWidthForSmallResolution={true}>
            <div>
                {projectTemplatesWithValues.AnyMissingValues && <MissingValuesCallout filterState={filterState} setFilterState={setFilterState}/>}
                <ProjectTenantVariableTemplatesFiltersBar totalVariablesCount={projectTemplatesWithValues.TotalValuesCount} filteredVariablesCount={projectTemplatesWithValues.FilteredValuesCount} filterState={filterState} onFilterChange={setFilterState} filterData={filterData} unsavedChangesCount={unsavedChanges} doBusyTask={doBusyTask}/>
                <ScopedProjectTemplatesDataTable values={variableValues} project={project} accounts={projectTemplatesWithValues.Accounts} certificates={projectTemplatesWithValues.Certificates} workerPools={projectTemplatesWithValues.WorkerPools} connectedEnvironments={connectedEnvironments} resourceOptions={resourceOptions} doBusyTask={doBusyTask} variableValueTracker={variableValueTracker} scopeChangeTracker={scopeChangeTracker}/>
                <div className={pagingBarStyles}>
                    <NumberedPagingBar totalItems={projectTemplatesWithValues.FilteredValuesCount} pageNumber={filterState.pageNumber} pageSize={pageSize} pageSizeOptions={[30, 50, 100]} onPagingSelectionChange={setPageState}/>
                </div>
            </div>
        </SidebarLayout>);
}
interface ScopedProjectTemplatesDataTableProps {
    accounts: {
        [accountId: string]: string;
    };
    certificates: {
        [certificateId: string]: string;
    };
    workerPools: {
        [workerPoolId: string]: WorkerPoolResource;
    };
    connectedEnvironments: Map<string, EnvironmentResource[]>;
    project: ProjectResource;
    values: ScopedProjectTemplateRowData[];
    resourceOptions: TenantVariableResourceOptions;
    doBusyTask: DoBusyTask;
    variableValueTracker: ProjectTenantVariableValueChangeTracker;
    scopeChangeTracker: ScopedProjectTemplatesScopeChangeTracker;
}
function ScopedProjectTemplatesDataTable({ values, project, accounts, certificates, workerPools, connectedEnvironments, resourceOptions, doBusyTask, variableValueTracker, scopeChangeTracker }: ScopedProjectTemplatesDataTableProps) {
    const [focusedValueKey, setFocusedValueKey] = useState<ProjectTenantVariableValueKey | null>(null);
    const [focusedScopeKey, setFocusedScopeKey] = useState<ScopedProjectTemplatesScopeKey | null>(null);
    const bindableTemplateNames = project.Templates.map((t) => t.Name);
    const editingPermissions = {
        permission: Permission.VariableEdit,
        project: project.Id,
        wildcard: true,
    };
    const hasEditPermissions = isAllowed(editingPermissions);
    const groupsWithEmptyRow = new Set<string>();
    for (const row of values) {
        const effectiveValue = variableValueTracker.has(row.RowKey) ? variableValueTracker.get(row.RowKey) : row.Variable.Value;
        if (!effectiveValue) {
            groupsWithEmptyRow.add(getGroupKey(row));
        }
    }
    const scopedEnvironmentIdsByGroup = buildScopedEnvironmentIdsByGroup(values.map((row) => ({
        groupKey: getGroupKey(row),
        environmentIds: scopeChangeTracker.has(row.RowKey) ? (scopeChangeTracker.get(row.RowKey) ?? []) : row.Scope.EnvironmentIds,
    })));
    const renderValueCell = (data: ScopedProjectTemplateRowData) => {
        const tenantId = data.Variable.TenantId;
        const sourceItems = resourceOptions.get(tenantId);
        const valueKey = data.RowKey;
        const hasValueChanged = variableValueTracker.has(valueKey);
        const value = hasValueChanged ? variableValueTracker.get(valueKey) : data.Variable.Value;
        const handleOnBlur = () => {
            setFocusedValueKey((previous: ProjectTenantVariableValueKey | null) => (previous === valueKey ? null : previous));
        };
        return (<ScopedProjectTemplateValueInlineCell template={data.Template} accounts={accounts} certificates={certificates} workerPools={workerPools} value={value} valueType={data.VariableType} sourceItems={sourceItems} doBusyTask={doBusyTask} valueTracker={variableValueTracker} valueKey={valueKey} focused={focusedValueKey === valueKey} onFocus={() => setFocusedValueKey(valueKey)} onBlur={handleOnBlur} bindableTemplateNames={bindableTemplateNames} hasEditPermissions={hasEditPermissions} hasValueChanged={hasValueChanged} initialValue={data.Variable.Value}/>);
    };
    const renderScopeCell = (data: ScopedProjectTemplateRowData) => {
        const scopeKey = data.RowKey;
        const hasScopeChanged = scopeChangeTracker.has(scopeKey);
        const value = variableValueTracker.has(data.RowKey) ? variableValueTracker.get(data.RowKey) : data.Variable.Value;
        const groupKey = getGroupKey(data);
        const groupEnvIds = scopedEnvironmentIdsByGroup.get(groupKey) ?? new Set<string>();
        const currentEnvIds = new Set(scopeChangeTracker.has(data.RowKey) ? (scopeChangeTracker.get(data.RowKey) ?? []) : data.Scope.EnvironmentIds);
        const availableEnvironments = (connectedEnvironments.get(data.Variable.TenantId) || []).filter((e) => !groupEnvIds.has(e.Id) || currentEnvIds.has(e.Id));
        const handleOnBlur = () => {
            setFocusedScopeKey((previous: ScopedProjectTemplatesScopeKey | null) => (previous === scopeKey ? null : previous));
        };
        return (<ScopedProjectTemplateScopeInlineCell environments={availableEnvironments} scopeTracker={scopeChangeTracker} scopeKey={scopeKey} focused={focusedScopeKey === scopeKey} onFocus={() => setFocusedScopeKey(scopeKey)} onBlur={handleOnBlur} hasEditPermissions={hasEditPermissions} canBeScoped={!!value} hasScopeChanged={hasScopeChanged} initialEnvironmentScope={data.Scope.EnvironmentIds}/>);
    };
    function onAddRow(data: ScopedProjectTemplateRowData) {
        const guid = generateGuid();
        const rowKey = createProjectTenantVariableKey(project.Id, data.Template.Id, data.Variable.TenantId, "", guid);
        variableValueTracker.add(rowKey, undefined);
        scopeChangeTracker.add(rowKey, []);
    }
    function onDeleteRow(values: ScopedProjectTemplateRowData[], data: ScopedProjectTemplateRowData) {
        const isLastTenantVariable = values.filter((v) => v.Template.Id === data.Template.Id && v.Variable.TenantId === data.Variable.TenantId).length === 1;
        if (isLastTenantVariable) {
            variableValueTracker.set(data.RowKey, undefined);
            scopeChangeTracker.set(data.RowKey, []);
            return;
        }
        variableValueTracker.delete(data.RowKey);
        scopeChangeTracker.delete(data.RowKey);
    }
    const groupByTemplateName = (values: ScopedProjectTemplateRowData[]) => {
        const uniqueTemplates = new Set(values.map((v) => v.Template.Name));
        return Array.from(uniqueTemplates).map((templateName) => ({
            key: templateName,
            selector: (data: ScopedProjectTemplateRowData) => data.Template.Name,
            header: (<tr key={`template-header-${templateName}`} style={{ backgroundColor: themeTokens.color.background.secondary.default }}>
                    <td colSpan={5} style={{ padding: `${space[8]} ${space[16]} ${space[8]} ${space[16]}` }}>
                        <div className={styles.variableSetHeader}>{templateName}</div>
                    </td>
                </tr>),
        }));
    };
    return (<GroupedDataTable data={values} tableClassName={tenantVariablesDataTable} groupBy={groupByTemplateName(values)} columns={[
            {
                title: "Name",
                render: (data: ScopedProjectTemplateRowData) => (data.IsTemplateChild ? null : <TemplateNameCell template={data.Template}/>),
                width: "20%",
                isChild: (data: ScopedProjectTemplateRowData) => data.IsTemplateChild,
            },
            {
                title: "Tenant",
                isChild: (data: ScopedProjectTemplateRowData) => data.IsTenantChild,
                render: (data: ScopedProjectTemplateRowData) => data.IsTenantChild ? null : (<div onFocus={() => setFocusedValueKey(null)}>
                                <TenantNameCellWithLink spaceId={project.SpaceId} tenantId={data.Variable.TenantId} tenantName={data.Variable.TenantName} tenantLogoLink={data.Variable.TenantLogoLink} isDisabled={data.Variable.TenantIsDisabled} linkToPage="Variables"/>
                            </div>),
                width: "20%",
            },
            {
                title: "Value",
                render: renderValueCell,
                width: "20%",
                padding: "0",
                backgroundColor: (data: ScopedProjectTemplateRowData) => {
                    const hasValueChanged = variableValueTracker.has(data.RowKey);
                    return hasValueChanged ? themeTokens.color.callout.background.warning.default : undefined;
                },
            },
            {
                title: "Scope",
                width: "30%",
                render: renderScopeCell,
                backgroundColor: (data: ScopedProjectTemplateRowData) => {
                    const hasValueChanged = scopeChangeTracker.has(data.RowKey);
                    return hasValueChanged ? themeTokens.color.callout.background.warning.default : undefined;
                },
            },
            {
                title: "",
                render: (data: ScopedProjectTemplateRowData) => {
                    const groupKey = getGroupKey(data);
                    const canAdd = !groupsWithEmptyRow.has(groupKey);
                    return (<div className={styles.variablesOverflowMenu}>
                                {hasEditPermissions ? (<OverflowMenu accessibleName="variableOverflowMenu" menuItems={[
                                canAdd ? OverflowMenuItems.item("Add", () => onAddRow(data)) : OverflowMenuItems.disabledItem("Add", "Fill in the current row before adding another"),
                                OverflowMenuItems.item("Delete", () => {
                                    onDeleteRow(values, data);
                                }),
                            ]}/>) : null}
                            </div>);
                },
                width: "10%",
            },
        ]} getRowKey={(data: ScopedProjectTemplateRowData) => data.RowKey}/>);
}
interface ScopedProjectTemplateRowData {
    Variable: ScopedProjectTemplateTenantValue;
    Template: ActionTemplateParameterResource;
    VariableType: VariableType;
    RowKey: ProjectTenantVariableValueKey;
    Scope: {
        EnvironmentIds: string[];
    };
    IsTemplateChild: boolean;
    IsTenantChild: boolean;
}
function getGroupKey(item: ScopedProjectTemplateRowData): string {
    return `${item.Template.Id}:${item.Variable.TenantId}`;
}
function buildScopedProjectTemplateRowData(project: ProjectResource, projectTemplatesWithValues: GetScopedProjectTemplateValuesForProjectBffResponse, variableValueTracker: ProjectTenantVariableValueChangeTracker, scopeChangeTracker: ScopedProjectTemplatesScopeChangeTracker): ScopedProjectTemplateRowData[] {
    const templatesVariableType: Dictionary<VariableType> = {};
    const variableValues: ScopedProjectTemplateRowData[] = [];
    const projectTemplates = new Map(project.Templates.map((pt) => [pt.Id, pt]));
    const templatesWithChildren = new Set<string>();
    const removedVariables = variableValueTracker.removedVariables();
    // Iterate through the keyed Variables structure
    for (const [templateId, templateWithValues] of Object.entries(projectTemplatesWithValues.Variables)) {
        const tenantsWithChildren = new Set<string>();
        const template = templateWithValues.Template;
        const projectTemplate = projectTemplates.get(templateId);
        if (!projectTemplate) {
            logger.warn("Could not find project template with ID {id}", { id: templateId });
            continue;
        }
        const hasTemplateBeenAdded = templatesVariableType[templateId] !== undefined;
        if (!hasTemplateBeenAdded) {
            templatesVariableType[templateId] = getVariableTypeFromDisplaySettings(projectTemplate.DisplaySettings["Octopus.ControlType"]);
        }
        // Group variables by tenant
        const tenantVariablesMap = new Map<string, ScopedProjectTemplateTenantValue[]>();
        for (const variable of templateWithValues.Values) {
            const tenantId = variable.TenantId;
            if (!tenantVariablesMap.has(tenantId)) {
                tenantVariablesMap.set(tenantId, []);
            }
            const tenantVariables = tenantVariablesMap.get(tenantId);
            if (tenantVariables) {
                tenantVariables.push(variable);
            }
        }
        for (const [tenantId, tenantVariables] of tenantVariablesMap) {
            if (tenantVariables.length > 0) {
                const variable = tenantVariables[0];
                const newVariablesMap = variableValueTracker.newVariables(project.Id, variable.TemplateId, variable.TenantId);
                const newScopesMap = scopeChangeTracker.newVariables(project.Id, variable.TemplateId, variable.TenantId);
                for (const [key, newVar] of newVariablesMap) {
                    const { guid } = extractProjectTenantVariableIds(key);
                    const newVariable = {
                        ...variable,
                        Id: guid,
                        Value: newVar,
                        Scope: { EnvironmentIds: newScopesMap.get(key) || [] },
                    };
                    tenantVariablesMap.get(tenantId)?.push(newVariable);
                }
                const variablesToKeep = tenantVariables.filter((v) => {
                    const key = createProjectTenantVariableKey(project.Id, variable.TemplateId, variable.TenantId, "", v.Id);
                    return !removedVariables.has(key);
                });
                tenantVariablesMap.set(tenantId, variablesToKeep);
            }
        }
        for (const [tenantId, tenantVariables] of tenantVariablesMap) {
            for (const variable of tenantVariables) {
                const isTemplateHasChildren = templatesWithChildren.has(templateId);
                if (!isTemplateHasChildren) {
                    templatesWithChildren.add(templateId);
                }
                const isTenantHasChildren = tenantsWithChildren.has(tenantId);
                if (!isTenantHasChildren) {
                    tenantsWithChildren.add(tenantId);
                }
                const rowKey = createProjectTenantVariableKey(project.Id, templateId, tenantId, "", variable.Id);
                variableValues.push({
                    Template: projectTemplate,
                    VariableType: templatesVariableType[templateId],
                    Variable: variable,
                    RowKey: rowKey,
                    Scope: variable.Scope,
                    IsTemplateChild: isTemplateHasChildren,
                    IsTenantChild: isTenantHasChildren,
                });
            }
        }
    }
    variableValues.sort((a, b) => Number(a.Variable.TenantIsDisabled) - Number(b.Variable.TenantIsDisabled));
    return variableValues;
}
